<template>
  <tr>
    <td :class="{ shrink: version.revision_url }">
      <ItemId :item-id="version.id" />
    </td>
    <td>
      {{ version.version || '—' }}
    </td>
    <td>
      <span
        v-if="version.tag"
        class="tag"
        >{{ version.tag }}</span
      >
      <template v-else>—</template>
    </td>
    <td>
      <span
        v-if="version.branch"
        class="tag"
        >{{ version.branch }}</span
      >
      <template v-else>—</template>
    </td>
    <td>
      <span
        v-if="version.state"
        class="tag is-capitalized"
        :class="stateClass(version.state)"
      >
        {{ version.state }}
      </span>
    </td>
    <td>
      <DockerImageIID
        v-if="version.docker_image_iid"
        :iid="version.docker_image_iid"
      />
      <span v-else>—</span>
    </td>
    <td
      :title="version.created"
      class="shrink"
    >
      {{ created(version.created) }}
    </td>
    <td
      class="shrink"
      v-if="version.revision_url || selectable"
    >
      <div class="has-text-centered">
        <a
          v-if="version.revision_url"
          title="Git revision"
          class="button is-small"
          :href="version.revision_url"
          target="_blank"
        >
          <i class="icon-link"></i>
        </a>
        <!-- Allows to select a worker version -->
        <a
          v-if="selectable"
          class="button is-success is-small"
          :disabled="!isSelectable || null"
          v-on:click="select"
          :class="{ 'is-loading': loading }"
          :title="selectTitle"
        >
          <i class="icon-plus"></i>
        </a>
      </div>
    </td>
  </tr>
</template>

<script lang="ts">
import { mapActions, mapState } from 'pinia'
import { type PropType, defineComponent } from 'vue'

import ItemId from '@/components/ItemId.vue'
import { WORKER_VERSION_STATE_COLORS } from '@/config'
import { WorkerVersionState } from '@/enums'
import { ago, errorParser } from '@/helpers'
import { truncateMixin } from '@/mixins'
import { useNotificationStore, useProcessStore } from '@/stores'
import type { UUID } from '@/types'
import type { WorkerVersion } from '@/types/worker'

import DockerImageIID from './DockerImageIID.vue'

export default defineComponent({
  emits: {
    'selected-version': (value: WorkerVersion) =>
      typeof value === 'object' && value !== null && typeof value.id === 'string',
  },
  mixins: [truncateMixin],
  components: {
    DockerImageIID,
    ItemId,
  },
  props: {
    version: {
      type: Object as PropType<WorkerVersion>,
      required: true,
    },
    /**
     * Whether or not a button allows to select this worker version.
     * This will trigger `selected-version` events.
     *
     * Without `processId` or `local` set, any worker version can be selected.
     */
    selectable: {
      type: Boolean,
      default: false,
    },
    /**
     * If `selectable` is set, selecting the version will both cause an event
     * and create a WorkerRun on the specified process.
     *
     * Only available versions will be selectable.
     */
    processId: {
      type: String as PropType<UUID | null>,
      default: null,
    },
    /**
     * If true, only worker versions that do not have a revision attached
     * can be selected (for creating local worker runs).
     *
     * This has no effect if `selectable` is not set.
     */
    local: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    loading: false,
  }),
  computed: {
    ...mapState(useProcessStore, ['processWorkerRuns']),
    isSelectable(): boolean {
      // The button is not supposed to be displayed at all
      if (!this.selectable) return false
      // On local processes, only allow selecting versions without a revision
      if (this.local) return !this.version.revision_url
      // On other processes, only allow selecting available versions
      if (this.processId) return this.version.state === 'available'
      // Outside of the context of any process, anything can be selected
      return true
    },
    selectTitle(): string | undefined {
      if (this.processId) {
        if (this.isSelectable) return 'Add this version to the process'
        return 'This version is not available'
      }
      if (this.isSelectable) return 'Select this worker version'
      else if (this.local) return 'Only worker versions without a revision attached can be selected'
      return undefined
    },
  },
  methods: {
    ...mapActions(useProcessStore, ['createWorkerRun', 'deleteWorkerRun']),
    ...mapActions(useNotificationStore, ['notify']),
    created(date: string): string {
      return ago(new Date(date))
    },
    async select() {
      if (!this.isSelectable) return
      this.$emit('selected-version', this.version)
      if (this.loading || !this.processId) return
      this.loading = true
      try {
        await this.createWorkerRun(this.processId, { worker_version_id: this.version.id })
      } catch (err) {
        this.notify({ type: 'error', text: errorParser(err) })
      } finally {
        this.loading = false
      }
    },
    stateClass(state: WorkerVersionState) {
      return WORKER_VERSION_STATE_COLORS[state]
    },
  },
})
</script>
